import { Box, styled } from '@mui/material';
import classNames from 'classnames';
import { useMemo } from 'react';

import { EAircraftStatus, EMaoMaintenanceBadge, ESeverity } from '#shared';

import { useAircrafts } from '../../data-context';
import { getAircraftStatus, getAircraftSubStatus } from '../../helpers';

const PREFIX = 'TSMED-AircraftLegend';
const aircraftLegendClasses = {
  group: `${PREFIX}-group`,
  item: `${PREFIX}-item`,
  airportIcon: `${PREFIX}-airportIcon`,
  label: `${PREFIX}-label`,
  aircraftIcon: `${PREFIX}-aircraftIcon`,
};

const Root = styled(Box)(({ theme: { spacing, palette } }) => ({
  display: 'flex',
  alignItems: 'start',
  color: palette.grey[700],
  borderRadius: spacing(1.5),
  background: 'rgba(0, 0, 0, 0.30)',
  gap: spacing(4),
  lineHeight: '100%',
  [`.${aircraftLegendClasses.item}`]: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing(1),
    height: '100%',
    [`.${aircraftLegendClasses.aircraftIcon}`]: {
      width: 18,
      height: 18,
      [`&.time-severity-${ESeverity.LOW}`]: {
        color: 'white',
      },
      [`&.time-severity-${ESeverity.MINOR}`]: {
        color: palette.mint[500],
      },
      [`&.time-severity-${ESeverity.MAJOR}`]: {
        color: palette.yellow[500],
      },
      [`&.time-severity-${ESeverity.CRITICAL}`]: {
        color: palette.error[500],
      },
      [`&.time-severity-${ESeverity.NA}`]: {
        color: palette.error[500],
      },
      [`&.aircraft-status-${EAircraftStatus.Maintenance}`]: {
        color: palette.grey[700],
        [`&.maintenance-type-${EMaoMaintenanceBadge.AOG}`]: {
          color: palette.error[200],
        },
      },
      svg: {
        height: '100%',
        width: '100%',
        path: { fill: 'currentColor' },
      },
    },
    [`.${aircraftLegendClasses.label}`]: {},
  },
}));
export const AircraftsLegend = () => {
  const aircrafts = useAircrafts();

  const statuses = useMemo(
    () =>
      aircrafts.reduce<
        Record<
          | 'Available'
          | 'Maintenance'
          | 'AOG'
          | 'OnNonCase'
          | 'Not Available'
          | 'OnCase',
          number
        >
      >(
        (acc, aircraft) => {
          const status = getAircraftStatus(aircraft);
          const subStatus = getAircraftSubStatus(aircraft);
          if (status === EAircraftStatus.Maintenance) {
            if (subStatus === EMaoMaintenanceBadge.AOG) {
              acc.AOG += 1;
            } else {
              acc.Maintenance += 1;
            }
          } else if (status === EAircraftStatus.OnTrip) {
            if (subStatus === 'OnCase') {
              acc.OnCase += 1;
            } else {
              acc.OnNonCase += 1;
            }
          } else {
            acc[status] += 1;
          }

          return acc;
        },
        {
          Available: 0,
          Maintenance: 0,
          OnCase: 0,
          OnNonCase: 0,
          ['Not Available']: 0,
          AOG: 0,
        },
      ),
    [aircrafts],
  );
  return (
    <Root>
      <Box className={classNames(aircraftLegendClasses.item)}>
        <Box className={classNames(aircraftLegendClasses.label)}>
          Planes Available: {statuses.Available}/{aircrafts.length}
        </Box>
      </Box>
      <Box className={classNames(aircraftLegendClasses.item)}>
        <Box className={classNames(aircraftLegendClasses.label)}>
          On Case: {statuses.OnCase}
        </Box>
      </Box>
      {statuses.Maintenance ? (
        <Box className={classNames(aircraftLegendClasses.item)}>
          <Box className={classNames(aircraftLegendClasses.label)}>
            Maintenance: {statuses.Maintenance}
          </Box>
        </Box>
      ) : null}
      {statuses.AOG ? (
        <Box className={classNames(aircraftLegendClasses.item)}>
          <Box className={classNames(aircraftLegendClasses.label)}>
            AOG: {statuses.AOG}
          </Box>
        </Box>
      ) : null}
    </Root>
  );
};
